import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { 
  BookOpen, 
  Play, 
  CheckCircle, 
  Target, 
  Zap, 
  Star,
  ArrowRight,
  MessageSquare,
  Users,
  Clock,
  Lightbulb
} from 'lucide-react';

const Training = () => {
  const navigate = useNavigate();
  const { user, loading, getTotalModuleXP, getCompletionRate } = useUser();
  
  // Debug user data changes
  React.useEffect(() => {
    console.log('🔄 Training page user data changed:', {
      user: user,
      completedModules: user?.completedModules,
      loading
    });
  }, [user, loading]);

  // Show loading only if we're actually loading and there's a token
  if (loading && localStorage.getItem('token')) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  // If no user and no token, redirect to login
  if (!user) {
    navigate('/login', { state: { from: '/training' } });
    return null;
  }

  const trainingModules = [
    {
      id: 1,
      title: 'Debate Fundamentals',
      description: 'Master the essential building blocks of effective debate including structure, argumentation, and basic techniques.',
      duration: '60 min',
      difficulty: 'Beginner',
      xpReward: 200,
      topics: ['Introduction to Debate Structure', 'Constructing Basic Arguments', 'Opening Statements', 'Evidence Presentation', 'Basic Rebuttal Techniques', 'Closing Arguments Quiz'],
      completed: (user?.completedModules || []).includes(1),
      category: 'Core Skills',
      prerequisites: [],
      estimatedTime: '60-90 minutes',
      sections: 6
    },
    {
      id: 2,
      title: 'Logical Fallacies & Critical Thinking',
      description: 'Learn to identify, avoid, and counter common logical fallacies while strengthening your critical thinking skills.',
      duration: '90 min',
      difficulty: 'Intermediate',
      xpReward: 300,
      topics: ['Introduction to Logical Fallacies', 'Strawman Fallacy', 'Ad Hominem Fallacy', 'Slippery Slope Fallacy', 'Appeal to Emotion Fallacy', 'False Dichotomy Fallacy', 'Circular Reasoning Fallacy', 'Comprehensive Quiz'],
      completed: (user?.completedModules || []).includes(2),
      category: 'Critical Thinking',
      prerequisites: ['Debate Fundamentals'],
      estimatedTime: '90-120 minutes',
      sections: 8
    },
    {
      id: 3,
      title: 'Advanced Argumentation',
      description: 'Master complex argument structures including deductive and inductive reasoning, syllogisms, and the Toulmin model.',
      duration: '120 min',
      difficulty: 'Advanced',
      xpReward: 400,
      topics: ['Deductive Reasoning', 'Inductive Reasoning', 'Syllogisms', 'Toulmin Model', 'Argument Mapping', 'Causal Arguments', 'Analogical Reasoning', 'Statistical Arguments', 'Expert Testimony', 'Advanced Argumentation Quiz'],
      completed: (user?.completedModules || []).includes(3),
      category: 'Advanced Skills',
      prerequisites: ['Debate Fundamentals', 'Logical Fallacies'],
      estimatedTime: '120-150 minutes',
      sections: 10
    },
    {
      id: 4,
      title: 'Rhetorical Techniques & Persuasion',
      description: 'Master the art of persuasive speaking through rhetorical devices, emotional appeals, and audience adaptation.',
      duration: '75 min',
      difficulty: 'Intermediate',
      xpReward: 350,
      topics: ['Ethos, Pathos, Logos', 'Rhetorical Questions', 'Repetition and Parallelism', 'Analogies and Metaphors', 'Audience Analysis', 'Emotional Appeals', 'Rhetorical Techniques Quiz'],
      completed: (user?.completedModules || []).includes(4),
      category: 'Persuasion',
      prerequisites: ['Debate Fundamentals'],
      estimatedTime: '75-100 minutes',
      sections: 7
    },
    {
      id: 5,
      title: 'Rebuttal & Cross-Examination',
      description: 'Learn advanced techniques for effectively countering arguments and conducting powerful cross-examinations.',
      duration: '100 min',
      difficulty: 'Advanced',
      xpReward: 450,
      topics: ['Direct Refutation', 'Concession and Refutation', 'Cross-Examination Techniques', 'Evidence Challenges', 'Witness Preparation', 'Leading Questions', 'Impeachment Strategies', 'Rebuttal Organization', 'Rebuttal & Cross-Examination Quiz'],
      completed: (user?.completedModules || []).includes(5),
      category: 'Advanced Skills',
      prerequisites: ['Debate Fundamentals', 'Logical Fallacies'],
      estimatedTime: '100-130 minutes',
      sections: 9
    },
    {
      id: 6,
      title: 'Debate Formats & Rules',
      description: 'Comprehensive guide to major debate formats including Lincoln-Douglas, Parliamentary, Policy, and Public Forum.',
      duration: '150 min',
      difficulty: 'Intermediate',
      xpReward: 500,
      topics: ['Lincoln-Douglas Format', 'Parliamentary Debate', 'Policy Debate', 'Public Forum', 'IPDA Format', 'World Schools', 'Congressional Debate', 'Extemporaneous Speaking', 'Format-Specific Strategies', 'Time Structures', 'Judging Criteria', 'Debate Formats Quiz'],
      completed: (user?.completedModules || []).includes(6),
      category: 'Formats',
      prerequisites: ['Debate Fundamentals'],
      estimatedTime: '150-180 minutes',
      sections: 12
    },
    {
      id: 7,
      title: 'Evidence & Research',
      description: 'Master the art of finding, evaluating, and presenting credible evidence to strengthen your arguments.',
      duration: '80 min',
      difficulty: 'Intermediate',
      xpReward: 350,
      topics: ['Source Evaluation', 'Evidence Hierarchy', 'Citation Methods', 'Research Strategies', 'Database Searching', 'Fact-Checking Techniques', 'Statistical Analysis', 'Evidence & Research Quiz'],
      completed: (user?.completedModules || []).includes(7),
      category: 'Research',
      prerequisites: ['Debate Fundamentals'],
      estimatedTime: '80-110 minutes',
      sections: 8
    },
    {
      id: 8,
      title: 'Public Speaking & Delivery',
      description: 'Develop powerful speaking skills including voice projection, body language, and audience engagement.',
      duration: '90 min',
      difficulty: 'Intermediate',
      xpReward: 400,
      topics: ['Voice Projection', 'Body Language', 'Pacing and Rhythm', 'Audience Engagement', 'Confidence Building', 'Eye Contact', 'Gestures and Movement', 'Public Speaking Quiz'],
      completed: (user?.completedModules || []).includes(8),
      category: 'Delivery',
      prerequisites: ['Debate Fundamentals'],
      estimatedTime: '90-120 minutes',
      sections: 8
    },
    {
      id: 9,
      title: 'Ethics & Sportsmanship',
      description: 'Learn the ethical principles of debate, fair play, and maintaining integrity in competitive environments.',
      duration: '45 min',
      difficulty: 'Beginner',
      xpReward: 200,
      topics: ['Debate Ethics', 'Fair Play Principles', 'Sportsmanship', 'Academic Integrity', 'Respect for Opponents', 'Honest Argumentation', 'Ethics & Sportsmanship Quiz'],
      completed: (user?.completedModules || []).includes(9),
      category: 'Ethics',
      prerequisites: [],
      estimatedTime: '45-60 minutes',
      sections: 7
    },
    {
      id: 10,
      title: 'Tournament Preparation',
      description: 'Complete guide to preparing for debate tournaments including strategy, logistics, and mental preparation.',
      duration: '60 min',
      difficulty: 'Advanced',
      xpReward: 400,
      topics: ['Tournament Strategy', 'Mental Preparation', 'Logistics Planning', 'Performance Optimization', 'Stress Management', 'Team Coordination', 'Equipment Preparation', 'Tournament Preparation Quiz'],
      completed: (user?.completedModules || []).includes(10),
      category: 'Competition',
      prerequisites: ['Debate Fundamentals', 'Debate Formats'],
      estimatedTime: '60-90 minutes',
      sections: 8
    },
    {
      id: 11,
      title: 'Digital Debate & Online Platforms',
      description: 'Master the art of debating in digital environments, including online platforms, video conferencing, and virtual tournaments.',
      duration: '45 min',
      difficulty: 'Intermediate',
      xpReward: 350,
      topics: ['Online Platform Navigation', 'Digital Etiquette', 'Technical Setup', 'Virtual Body Language', 'Screen Presence', 'Digital Tools', 'Online Debate Formats', 'Digital Debate Quiz'],
      completed: (user?.completedModules || []).includes(11),
      category: 'Formats',
      prerequisites: ['Debate Fundamentals'],
      estimatedTime: '45-60 minutes',
      sections: 8
    },
    {
      id: 12,
      title: 'International Debate Styles',
      description: 'Explore debate formats and styles from around the world, including British Parliamentary, Asian Parliamentary, and World Schools formats.',
      duration: '75 min',
      difficulty: 'Advanced',
      xpReward: 450,
      topics: ['British Parliamentary', 'Asian Parliamentary', 'World Schools', 'International Rules', 'Cultural Differences', 'Global Perspectives', 'Format Comparisons', 'International Debate Quiz'],
      completed: (user?.completedModules || []).includes(12),
      category: 'Formats',
      prerequisites: ['Debate Formats & Rules'],
      estimatedTime: '75-90 minutes',
      sections: 8
    },
    {
      id: 13,
      title: 'Debate Psychology & Mental Game',
      description: 'Develop mental resilience, confidence, and psychological strategies for high-pressure debate situations.',
      duration: '50 min',
      difficulty: 'Intermediate',
      xpReward: 300,
      topics: ['Mental Preparation', 'Confidence Building', 'Stress Management', 'Focus Techniques', 'Emotional Control', 'Performance Psychology', 'Mental Toughness', 'Psychology Quiz'],
      completed: (user?.completedModules || []).includes(13),
      category: 'Core Skills',
      prerequisites: ['Debate Fundamentals'],
      estimatedTime: '50-65 minutes',
      sections: 8
    },
    {
      id: 14,
      title: 'Advanced Evidence Analysis',
      description: 'Learn to critically evaluate, synthesize, and present complex evidence and data in debate contexts.',
      duration: '65 min',
      difficulty: 'Advanced',
      xpReward: 500,
      topics: ['Data Analysis', 'Statistical Evidence', 'Expert Testimony', 'Source Evaluation', 'Evidence Synthesis', 'Fact-Checking', 'Evidence Presentation', 'Evidence Analysis Quiz'],
      completed: (user?.completedModules || []).includes(14),
      category: 'Research',
      prerequisites: ['Evidence & Research'],
      estimatedTime: '65-80 minutes',
      sections: 8
    },
    {
      id: 15,
      title: 'Debate Leadership & Team Management',
      description: 'Develop leadership skills for debate teams, including coaching, strategy development, and team coordination.',
      duration: '55 min',
      difficulty: 'Expert',
      xpReward: 550,
      topics: ['Team Leadership', 'Strategy Development', 'Coaching Techniques', 'Team Dynamics', 'Performance Management', 'Communication Skills', 'Leadership Styles', 'Leadership Quiz'],
      completed: (user?.completedModules || []).includes(15),
      category: 'Advanced Skills',
      prerequisites: ['Advanced Argumentation', 'Tournament Preparation'],
      estimatedTime: '55-70 minutes',
      sections: 8
    }
  ];

  const difficultyColors = {
    'Beginner': 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
    'Intermediate': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
    'Advanced': 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200',
    'Expert': 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
  };

  const categoryColors = {
    'Core Skills': 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
    'Critical Thinking': 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200',
    'Advanced Skills': 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
    'Persuasion': 'bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-200',
    'Formats': 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200',
    'Research': 'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200',
    'Delivery': 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200',
    'Ethics': 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200',
    'Competition': 'bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-200'
  };

  const completedModules = (user?.completedModules || []).length;
  const earnedXP = getTotalModuleXP();
  const completionRate = getCompletionRate();

  // Debug logging
  console.log('🔍 Training page debug:', {
    user: user,
    completedModules: user?.completedModules,
    completedModulesCount: completedModules,
    earnedXP,
    completionRate,
    totalTimeSpent: user?.totalTimeSpent
  });

  // Debug module completion checks
  trainingModules.forEach(module => {
    const isCompleted = (user?.completedModules || []).includes(module.id);
    console.log(`🔍 Module ${module.id} (${module.title}): completed = ${isCompleted}, includes check = ${(user?.completedModules || []).includes(module.id)}`);
  });

  // Group modules by category and sort by difficulty
  const difficultyOrder = {
    'Beginner': 1,
    'Intermediate': 2,
    'Advanced': 3,
    'Expert': 4
  };

  // Get unique categories in desired order
  const categoryOrder = [
    'Formats',
    'Core Skills',
    'Critical Thinking',
    'Persuasion',
    'Delivery',
    'Advanced Skills',
    'Ethics',
    'Research',
    'Competition'
  ];
  const modulesByCategory = {};
  categoryOrder.forEach(cat => { modulesByCategory[cat] = []; });
  trainingModules.forEach(module => {
    if (modulesByCategory[module.category]) {
      modulesByCategory[module.category].push(module);
    } else {
      modulesByCategory[module.category] = [module];
    }
  });
  // Sort modules in each category by difficulty
  Object.keys(modulesByCategory).forEach(cat => {
    modulesByCategory[cat].sort((a, b) => difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty]);
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Training Center</h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Master debate skills through comprehensive lessons, interactive exercises, and real-world practice scenarios
          </p>
        </motion.div>

        {/* Progress Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-4 gap-6 mb-12"
        >
          <div className="card text-center">
            <div className="text-3xl font-bold text-primary-600 mb-2">
              {completedModules}/{trainingModules.length}
            </div>
            <div className="text-gray-600 dark:text-gray-300">Modules Completed</div>
          </div>
          <div className="card text-center">
            <div className="text-3xl font-bold text-yellow-600 mb-2 flex items-center justify-center">
              <Zap className="w-6 h-6 mr-1" />
              {earnedXP}
            </div>
            <div className="text-gray-600 dark:text-gray-300">XP Earned</div>
          </div>
          <div className="card text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">
              {completionRate}%
            </div>
            <div className="text-gray-600 dark:text-gray-300">Completion Rate</div>
          </div>
          <div className="card text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">
              {Math.floor((user?.totalTimeSpent || 0) / 60)}h {(user?.totalTimeSpent || 0) % 60}m
            </div>
            <div className="text-gray-600 dark:text-gray-300">Time Spent</div>
          </div>
        </motion.div>

        {/* Learning Path */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
            <BookOpen className="w-6 h-6 mr-2" />
            Learning Path
          </h2>
          {/* Render modules grouped by category and sorted by difficulty */}
          {categoryOrder.map(category => (
            modulesByCategory[category] && modulesByCategory[category].length > 0 && (
              <div key={category} className="mb-10">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 mt-8 flex items-center">
                  <span className={`px-3 py-1 rounded-full text-base font-semibold ${categoryColors[category]}`}>{category}</span>
                </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {modulesByCategory[category].map((module, index) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className={`card cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  module.completed ? 'border-green-400 bg-green-100 dark:bg-green-900/20' : ''
                }`}
                onClick={() => navigate(`/training/module/${module.id}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${categoryColors[module.category]}`}>
                        {module.category}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${difficultyColors[module.difficulty]}`}>
                        {module.difficulty}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{module.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">{module.description}</p>
                  </div>
                  {module.completed && (
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                  )}
                </div>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {module.duration}
                    </div>
                    <div className="flex items-center">
                      <BookOpen className="w-4 h-4 mr-1" />
                      {module.sections} sections
                    </div>
                  </div>
                  {module.prerequisites.length > 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Prerequisites: {module.prerequisites.join(', ')}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-yellow-600">
                    <Zap className="w-4 h-4 mr-1" />
                    {module.xpReward} XP
                  </div>
                  <button className={`btn-primary text-sm px-3 py-1 ${module.completed ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`}> 
                    {module.completed ? 'Complete Again' : 'Start'}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
              </div>
            )
          ))}
        </motion.div>

        {/* Practice Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
            <Target className="w-6 h-6 mr-2" />
            Practice Exercises
          </h2>
                      <div className="grid md:grid-cols-3 gap-6">
            <div className="card text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Argument Construction</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">Practice building strong, logical arguments with proper structure and evidence</p>
              <button 
                className="btn-primary"
                onClick={() => navigate('/live-debate/room')}
              >
                Start Practice
              </button>
            </div>
            
            <div className="card text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Rebuttal Drills</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">Learn to effectively counter opposing arguments and strengthen your responses</p>
              <button 
                className="btn-primary"
                onClick={() => navigate('/live-debate/room')}
              >
                Start Practice
              </button>
            </div>
            
            <div className="card text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Fallacy Detection</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">Identify logical fallacies in real arguments and practice calling them out</p>
              <button 
                className="btn-primary"
                onClick={() => navigate('/live-debate/room')}
              >
                Start Practice
              </button>
            </div>
          </div>
        </motion.div>

        {/* Learning Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
            <Lightbulb className="w-6 h-6 mr-2" />
            Learning Tips
          </h2>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="card">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-3">
                  <span className="text-blue-600 dark:text-blue-400 font-bold">1</span>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Follow the Path</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Complete modules in order to build a strong foundation before moving to advanced topics.</p>
            </div>
            
            <div className="card">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mr-3">
                  <span className="text-green-600 dark:text-green-400 font-bold">2</span>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Practice Regularly</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Apply what you learn in live debates and practice sessions to reinforce your skills.</p>
            </div>
            
            <div className="card">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mr-3">
                  <span className="text-purple-600 dark:text-purple-400 font-bold">3</span>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Review and Reflect</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Take time to review completed modules and reflect on your debate performances.</p>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Test Your Skills?</h3>
            <p className="text-xl mb-6 text-gray-200">
              Apply what you've learned in a live debate session
            </p>
            <button 
              className="btn-primary bg-white text-primary-700 hover:bg-gray-100 text-lg px-8 py-4 flex items-center mx-auto"
              onClick={() => navigate('/live-debate/room')}
            >
              <Play className="w-5 h-5 mr-2" />
              Start Live Debate
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Training; 